import { type FC, type ReactNode } from 'react';
import { HERO_SIZE, type HeroKey, heroSrc } from './heroImages';

export interface PageHeroProps {
  heroKey: HeroKey;
  /** Match a narrow (max-w-4xl) public content column rather than the app page width. */
  narrow?: boolean;
  /** The banner is the first thing on the page: fetch it at high priority. */
  priority?: boolean;
  children: ReactNode;
}

/**
 * PageHero - a decorative banner image behind the page title. The picture is hidden from
 * assistive tech; the fade, scrim and vignette (all tokens) keep the title readable.
 */
export const PageHero: FC<PageHeroProps> = ({ heroKey, narrow, priority, children }) => (
  <div className="page-hero" data-hero={heroKey} data-narrow={narrow ? 'true' : undefined}>
    <picture aria-hidden="true">
      {(['avif', 'webp'] as const).map((format) => (
        <source
          key={format}
          type={`image/${format}`}
          srcSet={`${heroSrc(heroKey, 'sm', format)} 800w, ${heroSrc(heroKey, 'lg', format)} ${HERO_SIZE.width}w`}
          sizes="100vw"
        />
      ))}
      <img
        src={heroSrc(heroKey, 'lg', 'webp')}
        alt=""
        width={HERO_SIZE.width}
        height={HERO_SIZE.height}
        decoding="async"
        {...(priority ? { fetchPriority: 'high' as const } : {})}
        className="page-hero__img"
      />
    </picture>
    <div className="page-hero__shade" aria-hidden="true" />
    <div className="page-hero__inner">{children}</div>
  </div>
);
